import React, { useState, useMemo } from "react";
import { usePatientIntakePaging } from "../hooks/useClinic";
import { useTablePreferences } from "../hooks/useTablePreferences";
import { useListViews } from "../hooks/useListViews";
import { CustomButton } from "../components/ui/CustomButton";
import { User, GetPatientsIntakePayload, ListView, ListViewData, MeetingType, ClinicUserApiResponse } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ManageColumnsModal, ColumnConfig } from "../components/modals/ManageColumnsModal";
import { SaveViewModal } from "../components/modals/SaveViewModal";
import { MeetingCountModal } from "../components/modals/MeetingCountModal";
import { IssuesModal } from "../components/modals/IssuesModal";
import { EditCommentsModal } from "../components/modals/EditCommentsModal";
import { EditPatientModal } from "../components/modals/EditPatientModal";
import { NewMeetingModal } from "../components/modals/NewMeetingModal";
import { ViewMeetingsModal } from "../components/modals/ViewMeetingsModal";
import { FinishTreatmentModal } from "../components/modals/FinishTreatmentModal";
import { PaymentLinksModal } from "../components/modals/PaymentLinksModal";
import { AdHocMeetingModal } from "../components/modals/AdHocMeetingModal";
import { CallPatientConfModal } from "../components/modals/CallPatientConfModal";
import { ViewSelector } from "../components/ui/ViewSelector";
import { ContextMenu } from "../components/ui/ContextMenu";
import { WhatsAppConversationPopup } from "../components/whatsapp/WhatsAppConversationPopup";
import { normalizePhone } from "../utils/phoneUtils";
import apiService from "../services/api";
import { saveLastViewId, getLastViewId, clearLastViewId } from "../utils/viewPersistence";
import { canScheduleMeeting } from '../utils/permissions';
import {
    PATIENT_COLUMNS,
    formatDate,
    calculateFinishReason,
    formatInvitationStatus,
    formatTasksNumber,
    formatNextMeetingDateTime,
    formatMeetingCount,
    getAgeGroupColor,
    formatSubClinic,
    shouldHighlightSubClinic,
    formatNotes,
    formatMoveType,
} from "../utils/patientTableFormat";

//tr 

interface PatientsScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number, meetingId?: string | number) => void;
    users: ClinicUserApiResponse[];
    meetingTypes: MeetingType[];
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

export const PatientsScreen: React.FC<PatientsScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    users: propsUsers,
    meetingTypes: propsMeetingTypes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Check if user is admin first (needed for state initialization)
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    const [page, setPage] = useState(1);
    const [selectedClinicId, setSelectedClinicId] = useState<number>(0);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [pageSize] = useState(20);
    const [selectedStatus, setSelectedStatus] = useState<string>("all");
    const [userId, setUserId] = useState<number>(0);

    // Status list for filtering
    const STATUS_LIST = [
        { value: "all", label: "All Statuses" },
        { value: "Intake", label: "Intake" },
        { value: "Finish", label: "Finish" },
        { value: "Cancel", label: "Cancel" },
        { value: "New", label: "New" },
        { value: "Waiting-for-approval", label: "Waiting-for-approval" },
        { value: "Follow-Up", label: "Follow-Up" },
        { value: "Obligation-review-needed", label: "Obligation-review-needed" },
        { value: "Reject", label: "Reject" },
        { value: "Waiting-for-previus-patient-to-finish", label: "Waiting-for-previus-patient-to-finish" },
        { value: "Waiting-for-obligations", label: "Waiting-for-obligations" },
        { value: "Waiting-for-payment-followup", label: "Waiting-for-payment-followup" },
        { value: "Waiting-for-payment-intake-moved-back-to-hosen", label: "Waiting-for-payment-intake-moved-back-to-hosen" },
        { value: "Waiting-for-payment-intake", label: "Waiting-for-payment-intake" },
        { value: "Waiting-for-single-guardian-verification", label: "Waiting-for-single-guardian-verification" },
        { value: "Waiting-for-parent2-sign", label: "Waiting-for-parent2-sign" },
    ];

    // Define all available columns with their configurations
    // The full patients-table column set, shared with the patient search results table.
    const ALL_COLUMNS: ColumnConfig[] = PATIENT_COLUMNS;

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "created_at",
        sortOrder: "desc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'patients-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    const token = localStorage.getItem("token") || "";

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);

    // Track the base view that was loaded, even after currentViewId is cleared
    // This allows users to save changes back to the original view
    const [baseViewId, setBaseViewId] = useState<number | null>(null);
    const [isMeetingCountModalOpen, setIsMeetingCountModalOpen] = useState(false);
    const [selectedPatientForMeetings, setSelectedPatientForMeetings] = useState<any>(null);
    const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
    const [selectedPatientForIssues, setSelectedPatientForIssues] = useState<any>(null);
    const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
    const [selectedPatientForEdit, setSelectedPatientForEdit] = useState<any>(null);
    const [isSavingComments, setIsSavingComments] = useState(false);
    const [isNewMeetingModalOpen, setIsNewMeetingModalOpen] = useState(false);
    const [selectedPatientForNewMeeting, setSelectedPatientForNewMeeting] = useState<any>(null);
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [selectedPatientForEditInfo, setSelectedPatientForEditInfo] = useState<any>(null);
    const [isSavingPatientInfo, setIsSavingPatientInfo] = useState(false);
    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [isViewMeetingsModalOpen, setIsViewMeetingsModalOpen] = useState(false);
    const [selectedPatientForViewMeetings, setSelectedPatientForViewMeetings] = useState<any>(null);
    const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
    const [selectedPatientForFinishTreatment, setSelectedPatientForFinishTreatment] = useState<any>(null);
    const [selectedPatientForPaymentLinks, setSelectedPatientForPaymentLinks] = useState<any>(null);
    const [selectedPatientForConfCall, setSelectedPatientForConfCall] = useState<any>(null);
    const [selectedPatientForAdHoc, setSelectedPatientForAdHoc] = useState<any>(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; patient: any } | null>(null);
    const [waPopupPatient, setWaPopupPatient] = useState<{ phone: string; name?: string; hmId?: string; id?: number } | null>(null);

    // Status message state
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null);

    // List Views integration
    const {
        privateViews,
        publicViews,
        defaultView,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
        isCreating,
    } = useListViews({
        userId: user?.id as number,
        listName: 'patients',
        token,
        enabled: !!user?.id && !!token,
    });

    // Track if we've already loaded the initial view to prevent re-loading when view is cleared
    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    React.useEffect(() => {
        // Only run after views are loaded
        if (isLoadingViews) {
            return;
        }

        // Only auto-load view once on initial mount
        if (hasLoadedInitialView.current) {
            return;
        }

        const savedViewId = getLastViewId('patients');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                // View was deleted, clear the saved ID
                clearLastViewId('patients');
            }
        } else if (defaultView) {
            // No saved view, use default view if one exists
            handleLoadView(defaultView);
            hasLoadedInitialView.current = true;
        } else {
            // No view to load, mark as loaded to prevent future auto-loads
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews, defaultView]);

    // The user filter is admin-only, so a non-admin must never carry a user_id of
    // someone else - fall back to their own patients.
    React.useEffect(() => {
        if (!isAdmin) {
            if (userId !== 0) setUserId(0);
        }
    }, [isAdmin, userId]);


    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);


    // Sync preferences when state changes
    React.useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    // Use clinics from props
    const clinics = useMemo(
        () => propsClinics?.items || [],
        [propsClinics?.items]
    );


    // Use users from props instead of fetching separately
    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        propsUsers.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [propsUsers]);

    const getUserName = (userId: number): string => {
        return userNameMap[userId] || "";
    };

    // Use clinics from props to create a clinic name map
    const clinicsMap = useMemo(() => {
        const map: Record<number, string> = {};
        clinics.forEach((c: any) => {
            map[c.id] = c.name;
        });
        return map;
    }, [clinics]);

    const getClinicName = (clinicId: number): string => {
        return clinicsMap[clinicId] || "";
    };


    const patientsPayload: GetPatientsIntakePayload = useMemo(
        () => {
            // The list is scoped to the signed-in user by default - what the removed
            // "Only mine" checkbox used to do when checked. An admin picking someone
            // in the user filter overrides that; there is no longer a way to ask for
            // every user's patients at once.
            const currentUserId = typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10);
            const requestUserId = userId !== 0 ? userId : currentUserId;

            return {
                token,
                clinic_id: selectedClinicId,
                offset: (page - 1) * pageSize,
                pageSize,
                search: search,
                status: selectedStatus,
                user_id: requestUserId,
                sub_clinic: "all",
                source: "all",
                sortedBy: [{ sortBy: sortColumn, orderBy: sortOrder }],
            };
        },
        [token, selectedClinicId, search, page, pageSize, sortColumn, sortOrder, selectedStatus, userId, user.id]
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleSearchClick = () => {
        setSearch(searchInput);
        setPage(1);
    };

    const patientsQuery = usePatientIntakePaging(patientsPayload);

    // Calculate pagination
    const totalPatients = patientsQuery.data?.total_count || 0;
    const currentPatients = patientsQuery.data?.items || [];
    const hasNextPage = (page - 1) * pageSize + currentPatients.length < totalPatients;

    const handleRefresh = () => {
        patientsQuery.refetch();
    };

    // Transformation functions based on guidelines

    // Helper function to check if user can save to the current view
    // Only clears currentViewId if user doesn't have permission to save (public view + not admin)
    const clearViewOnChange = () => {
        if (currentViewId === null) return;

        const allViews = [...privateViews, ...publicViews];
        const currentView = allViews.find(v => v.id === currentViewId);

        if (!currentView) return;

        const isPrivateView = currentView.type === 'private';
        const isPublicView = currentView.type === 'public';
        const canSaveToPublicView = isAdmin;

        const canSaveToView = isPrivateView || (isPublicView && canSaveToPublicView);

        if (!canSaveToView) {
            setCurrentViewId(null);
            clearLastViewId('patients');
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
        setPage(1);
        clearViewOnChange();
    };



    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);

    // Column reordering handlers with long press detection




    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't sort if clicking on resize handle
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-gray-100 cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-blue-600">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                {/* Resize handle */}
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        )
            ;
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        // Also reset filters
        setSearch("");
        setSearchInput("");
        setSelectedClinicId(0);
        setSelectedStatus("all");

        // Clear saved view
        setCurrentViewId(null);
        clearLastViewId('patients');

        resetToDefaults();
    };

    // View management handlers
    const handleUpdateView = async () => {
        // Use baseViewId since currentViewId might be cleared after manual changes
        if (!baseViewId) return;

        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            filters: {
                search,
                selectedClinicId,
                selectedStatus,
            },
        };

        try {
            await updateListView({
                id: baseViewId,
                data: viewData,
            });

            // After saving, restore currentViewId to baseViewId to reflect the saved state
            setCurrentViewId(baseViewId);
            saveLastViewId('patients', baseViewId);
            hasLoadedInitialView.current = true;
        } catch (error) {
            console.error('Failed to update view:', error);
        }
    };

    const handleSaveAsView = async (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => {
        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            description,
            filters: {
                search,
                selectedClinicId,
                selectedStatus,
            },
            ...(isDefault && { is_default: true }),
        };

        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'patients',
                data: viewData,
            });

            // Set both IDs to the newly created view
            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('patients', newView.id);
            hasLoadedInitialView.current = true;

            setIsSaveViewModalOpen(false);
        } catch (error) {
            console.error('Failed to save view:', error);
            // Could show an error toast here
        }
    };

    const handleLoadView = (view: ListView) => {
        // Merge in any columns added since this view was saved so they still appear in Manage Columns.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter(id => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);

        // Check if clinic will change
        const newClinicId = view.data.filters?.selectedClinicId ?? 0;
        const clinicChanged = newClinicId !== selectedClinicId;

        // Always apply filters from the view (or clear if not defined)
        if (view.data.filters) {
            const savedSearch = view.data.filters.search ?? "";
            setSearch(savedSearch);
            setSearchInput(savedSearch);
            setSelectedClinicId(newClinicId);
            setSelectedStatus(view.data.filters.selectedStatus ?? "all");
        } else {
            // Clear filters if the view doesn't have them defined
            setSearch("");
            setSearchInput("");
            setSelectedClinicId(0);
            setSelectedStatus("all");
        }

        setCurrentViewId(view.id);
        setBaseViewId(view.id); // Track the base view for save functionality
        saveLastViewId('patients', view.id);
        setPage(1); // Reset to first page when changing view

        // Refetch data if clinic changed
        if (clinicChanged) {
            // Use setTimeout to ensure state updates are processed first
            setTimeout(() => {
                patientsQuery.refetch();
            }, 0);
        }
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                // If we deleted the current view, reset to defaults
                handleResetColumns();
                setCurrentViewId(null);
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    const renderPatientsTab = () => {
        if (patientsQuery.isLoading) {
            return (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading patients...</p>
                </div>
            );
        }

        if (patientsQuery.isError) {
            return (
                <div className="text-center py-12 text-red-600">
                    <p>Error loading patients. Please try again.</p>
                    <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        className="mt-4"
                    >
                        Retry
                    </CustomButton>
                </div>
            );
        }

        const patients = patientsQuery.data?.items || [];

        return (
            <div className="space-y-4">
                {patients.length === 0 ? (
                    <div className="text-center py-12 text-gray-600">
                        <p>No patients found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                        const columnConfig: Record<string, { label: string; nonSortable?: boolean }> = {
                                            "HM_ID": { label: "HM ID" },
                                            "first_name": { label: "First name" },
                                            "last_name": { label: "Last name" },
                                            "status": { label: "Status" },
                                            "finish_reason": { label: "Finish reason", nonSortable: true },
                                            "invitation_status": { label: "Invitation status" },
                                            "issues": { label: "Issues", nonSortable: true },
                                            "case_manager_id": { label: "Case Manager" },
                                            "doctor_id": { label: "Psychiatrist" },
                                            "clinic_id": { label: "Clinic" },
                                            "patient_type": { label: "Age group" },
                                            "sub_clinic": { label: "Sub clinic" },
                                            "meeting_count": { label: "Meeting number", nonSortable: true },
                                            "notes": { label: "Notes", nonSortable: true },
                                            "last_interaction": { label: "Last meeting" },
                                            "next_meeting_start": { label: "Next meeting" },
                                            "funding_entity": { label: "Funding entity" },
                                            "created_at": { label: "Created date" },
                                            "move_clinic_status": { label: "Move clinic..." },
                                            "move_type": { label: "Move type" },
                                            "payment_link": { label: "Payment link", nonSortable: true },
                                            "area": { label: "Area" },
                                            "contact_name": { label: "Contact Name" },
                                            "contact_phone": { label: "Contact Phone" },
                                            "contact_role": { label: "Contact Role" },
                                            "cancel_reason": { label: "Cancel Reason" },
                                            "cancel_notes": { label: "Cancel Notes", nonSortable: true }
                                        };

                                        const config = columnConfig[columnId];
                                        return (
                                            <ResizableHeader
                                                key={columnId}
                                                column={columnId}
                                                label={config.label}
                                                nonSortable={config.nonSortable}
                                            />
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {patients.map((patient: any) => {
                                    const finishReason = calculateFinishReason(patient);
                                    const invitationStatus = formatInvitationStatus(patient);
                                    const tasksNumber = formatTasksNumber(patient);
                                    const caseManagerName = getUserName(patient.case_manager_id);
                                    const doctorName = getUserName(patient.doctor_id);
                                    const ageGroupColor = getAgeGroupColor(patient.patient_type);
                                    const subClinic = formatSubClinic(patient);
                                    const isSubClinicHighlighted = shouldHighlightSubClinic(patient);
                                    const meetingCount = formatMeetingCount(patient);
                                    const notes = formatNotes(patient.task);
                                    const lastMeeting = formatDate(patient.last_interaction);
                                    const nextMeeting = formatNextMeetingDateTime(patient);
                                    const createdDate = formatDate(patient.created_at);

                                    const getCellContent = (columnId: string) => {
                                        switch (columnId) {
                                            case "HM_ID":
                                                return patient.HM_ID ? (
                                                    <button
                                                        data-ai-id="patients-patient-id-link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onNavigate?.("patient", patient.id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {patient.HM_ID}
                                                    </button>
                                                ) : "-";
                                            case "first_name":
                                                return patient.first_name ? (
                                                    <button
                                                        data-ai-id="patients-patient-name-link"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            setIsLoadingPatientData(true);

                                                            // Small delay to ensure spinner is visible
                                                            await new Promise(resolve => setTimeout(resolve, 100));

                                                            try {
                                                                const patientData = await apiService.getPatientPage({
                                                                    token,
                                                                    patient_id: patient.id
                                                                });
                                                                setSelectedPatientForEditInfo(patientData);
                                                                setIsEditPatientModalOpen(true);
                                                            } catch (error) {
                                                                console.error('Failed to fetch patient data:', error);
                                                            } finally {
                                                                setIsLoadingPatientData(false);
                                                            }
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                        disabled={isLoadingPatientData}
                                                    >
                                                        {patient.first_name}
                                                    </button>
                                                ) : "-";
                                            case "last_name":
                                                return patient.last_name || "-";
                                            case "status":
                                                return patient.status ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                                                        {patient.status}
                                                    </span>
                                                ) : "-";
                                            case "finish_reason":
                                                return finishReason ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                                                        {finishReason}
                                                    </span>
                                                ) : "-";
                                            case "invitation_status":
                                                return invitationStatus ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                                                        {invitationStatus}
                                                    </span>
                                                ) : "-";
                                            case "issues":
                                                if (!tasksNumber || tasksNumber === "-") return "-";

                                                return (
                                                    <button
                                                        data-ai-id="patients-task-count-link"
                                                        onClick={() => {
                                                            setSelectedPatientForIssues(patient);
                                                            setIsIssuesModalOpen(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {tasksNumber}
                                                    </button>
                                                );
                                            case "case_manager_id":
                                                return caseManagerName || "-";
                                            case "doctor_id":
                                                return doctorName || "-";
                                            case "clinic_id":
                                                return getClinicName(patient.clinic_id) || "-";
                                            case "patient_type":
                                                return patient.patient_type ? (
                                                    <span
                                                        className="px-2 py-1 text-xs rounded text-white"
                                                        style={{ backgroundColor: ageGroupColor }}
                                                    >
                                                        {patient.patient_type}
                                                    </span>
                                                ) : "-";
                                            case "sub_clinic":
                                                return subClinic || "-";
                                            case "meeting_count":
                                                return meetingCount ? (
                                                    <button
                                                        data-ai-id="patients-meeting-count-link"
                                                        onClick={() => {
                                                            setSelectedPatientForMeetings(patient);
                                                            setIsMeetingCountModalOpen(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {meetingCount}
                                                    </button>
                                                ) : "-";
                                            case "notes":
                                                return (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedPatientForEdit(patient);
                                                                setIsEditCommentsModalOpen(true);
                                                            }}
                                                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                                            title="Edit notes"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <span className="truncate max-w-xs" title={notes}>{notes}</span>
                                                    </div>
                                                );
                                            case "last_interaction":
                                                return lastMeeting || "-";
                                            case "next_meeting_start":
                                                return nextMeeting || "-";
                                            case "funding_entity":
                                                return patient.funding_entity ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                                                        {patient.funding_entity}
                                                    </span>
                                                ) : "-";
                                            case "created_at":
                                                return createdDate || "-";
                                            case "move_clinic_status":
                                                return patient.move_clinic_status && patient.move_clinic_status !== 'none' ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-teal-100 text-teal-700">
                                                        {patient.move_clinic_status}
                                                    </span>
                                                ) : "-";
                                            case "move_type":
                                                return formatMoveType(patient.move_type) || "-";
                                            case "payment_link":
                                                return patient.payment_link ? (
                                                    <a
                                                        href={patient.payment_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 underline"
                                                    >
                                                        Link
                                                    </a>
                                                ) : "-";
                                            case "area":
                                                return patient.area || "-";
                                            case "contact_name":
                                                return patient.contact_name || "-";
                                            case "contact_phone":
                                                return patient.contact_phone || "-";
                                            case "contact_role":
                                                return patient.contact_role || "-";
                                            case "cancel_reason":
                                                return patient.cancel_reason || "-";
                                            case "cancel_notes":
                                                return patient.cancel_notes ? (
                                                    <span className="truncate max-w-xs" title={patient.cancel_notes}>
                                                        {patient.cancel_notes}
                                                    </span>
                                                ) : "-";
                                            default:
                                                return "-";
                                        }
                                    };

                                    return (
                                        <tr
                                            key={patient.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={(e) => {
                                                // Don't open context menu if clicking on buttons or links
                                                const target = e.target as HTMLElement;
                                                if (
                                                    target.tagName === 'BUTTON' ||
                                                    target.tagName === 'A' ||
                                                    target.closest('button') ||
                                                    target.closest('a')
                                                ) {
                                                    return;
                                                }

                                                setContextMenu({
                                                    x: e.clientX,
                                                    y: e.clientY,
                                                    patient: patient
                                                });
                                            }}
                                        >
                                            {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                                const isSubClinicCol = columnId === "sub_clinic";

                                                return (
                                                    <td
                                                        key={columnId}
                                                        className={`px-4 py-3 whitespace-nowrap text-sm ${isSubClinicCol && isSubClinicHighlighted ? 'text-red-600 font-medium' : 'text-gray-900'}`}
                                                    >
                                                        {getCellContent(columnId)}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="patients"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            {/* Patients Section */}
            <div className="bg-white rounded-lg shadow mb-8">
                <div className="border-b border-gray-200 px-6 py-4">
                    {/* Header row with title and view selector */}
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Patients</h2>
                            <ViewSelector
                                privateViews={privateViews}
                                publicViews={publicViews}
                                currentViewId={currentViewId}
                                baseViewId={baseViewId}
                                onSelectView={handleLoadView}
                                onSaveView={handleUpdateView}
                                onSaveAsView={() => setIsSaveViewModalOpen(true)}
                                onDeleteView={handleDeleteView}
                                isLoading={isLoadingViews}
                                isAdmin={isAdmin}
                            />
                        </div>
                        <CustomButton
                            variant="secondary"
                            size="sm"
                            onClick={handleManageColumns}
                        >
                            ⚙️ Manage Columns
                        </CustomButton>
                    </div>

                    {/* Controls row with Search, Clinic filter, and Manage Columns */}
                    <div className="flex gap-4 items-center">
                        <div className="flex-1">
                            <div className="flex gap-2">
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="Search by name, email, or phone..."
                                    value={searchInput}
                                    onChange={handleSearchChange}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchClick();
                                        }
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    onClick={handleSearchClick}
                                    className="px-4 py-2 rounded-md text-gray-700 font-medium transition-all duration-200 hover:shadow-lg hover:bg-opacity-20"
                                    style={{
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(209, 213, 219, 0.3)',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                        background: 'rgba(249, 250, 251, 0.6)',
                                    }}
                                    title="Search"
                                >
                                    🔍
                                </button>
                            </div>
                        </div>
                        <div className="w-64">
                            <select
                                id="clinic-select"
                                value={selectedClinicId}
                                onChange={(e) => {
                                    setSelectedClinicId(Number(e.target.value));
                                    setSearch(searchInput);
                                    setPage(1);
                                }}
                                disabled={false}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value={0}>All Clinics</option>
                                {clinics.map((clinic: any) => (
                                    <option key={clinic.id} value={clinic.id}>
                                        {clinic.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="w-64">
                            <select
                                id="status-select"
                                value={selectedStatus}
                                onChange={(e) => {
                                    setSelectedStatus(e.target.value);
                                    setSearch(searchInput);
                                    setPage(1);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                {STATUS_LIST.map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* User filter - only show for admins */}
                        {isAdmin && (
                            <div className="w-64">
                                <select
                                    id="user-select"
                                    value={userId}
                                    onChange={(e) => {
                                        setUserId(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {/* 0 is no longer "everyone" - the list falls back to the
                                        signed-in user's own patients. */}
                                    <option value={0}>My Patients</option>
                                    {propsUsers.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name || u.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderPatientsTab()}
                </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-center gap-2 mt-6">
                <CustomButton
                    variant="secondary"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    Previous
                </CustomButton>
                <span className="px-4 py-2 text-sm text-gray-600">
                    Page {page} of {Math.ceil((patientsQuery.data?.total_count || 0) / pageSize)}
                </span>
                <CustomButton
                    variant="secondary"
                    size="sm"
                    disabled={!hasNextPage}
                    onClick={() => setPage(page + 1)}
                >
                    Next
                </CustomButton>
            </div>

            {/* Manage Columns Modal */}
            <ManageColumnsModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={columnOrder.map(id => ({
                    id,
                    label: ALL_COLUMNS.find(col => col.id === id)?.label || id,
                    visible: columnVisibility[id] ?? true,
                }))}
                onSave={handleSaveColumns}
                onReset={handleResetColumns}
            />

            {/* Save As View Modal */}
            <SaveViewModal
                isOpen={isSaveViewModalOpen}
                onClose={() => setIsSaveViewModalOpen(false)}
                onSave={handleSaveAsView}
                currentConfig={{
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                }}
                isAdmin={isAdmin}
                isSaving={isCreating}
                existingDefaultView={defaultView}
            />

            {/* Meeting Count Modal */}
            <MeetingCountModal
                isOpen={isMeetingCountModalOpen}
                onClose={() => {
                    setIsMeetingCountModalOpen(false);
                    setSelectedPatientForMeetings(null);
                }}
                caseManagerIntake={selectedPatientForMeetings?.meeting_count_text1 || "0"}
                psychiatricIntake={selectedPatientForMeetings?.meeting_count_text2 || "0"}
                psyFollowUp={selectedPatientForMeetings?.meeting_count_text3 || "0"}
                caseManagerFollowUp={selectedPatientForMeetings?.meeting_count_text4 || "0"}
            />

            <IssuesModal
                isOpen={isIssuesModalOpen}
                onClose={() => {
                    setIsIssuesModalOpen(false);
                    setSelectedPatientForIssues(null);
                }}
                issues={selectedPatientForIssues?.issues ? JSON.parse(selectedPatientForIssues.issues) : []}
                getUserName={getUserName}
            />

            {/* Edit Comments Modal */}
            <EditCommentsModal
                isOpen={isEditCommentsModalOpen}
                onClose={() => {
                    setIsEditCommentsModalOpen(false);
                    setSelectedPatientForEdit(null);
                }}
                initialNotes={selectedPatientForEdit?.task || ''}
                onSave={async (newNotes: string) => {
                    setIsSavingComments(true);
                    try {
                        await apiService.updatePatientComments(
                            token,
                            selectedPatientForEdit.id,
                            newNotes
                        );
                        // Refresh patient data
                        await patientsQuery.refetch();
                        // Close modal
                        setIsEditCommentsModalOpen(false);
                        setSelectedPatientForEdit(null);
                    } catch (error: any) {
                        console.error('Failed to update comments:', error);
                        throw new Error(error.response?.data?.message || 'Failed to update comments');
                    } finally {
                        setIsSavingComments(false);
                    }
                }}
                isSaving={isSavingComments}
            />

            {/* Context Menu */}
            {contextMenu && (() => {
                const hasPhoneExt = user.phone_ext && user.phone_ext.trim() !== '';
                const patientPhone = contextMenu.patient.phone;

                return (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        options={[
                            {
                                label: 'Edit',
                                icon: '✏️',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    setIsLoadingPatientData(true);
                                    try {
                                        const patientData = await apiService.getPatientPage({
                                            token,
                                            patient_id: patient.id
                                        });
                                        setSelectedPatientForEditInfo(patientData);
                                        setIsEditPatientModalOpen(true);
                                    } catch (error) {
                                        console.error('Failed to fetch patient data:', error);
                                    } finally {
                                        setIsLoadingPatientData(false);
                                    }
                                }
                            },
                            ...(canScheduleMeeting(user.type) ? [{
                                label: 'New Meeting',
                                icon: '📅',
                                onClick: () => {
                                    setSelectedPatientForNewMeeting(contextMenu.patient);
                                    setIsNewMeetingModalOpen(true);
                                    setContextMenu(null);
                                }
                            }] : []),
                            {
                                label: 'View Meetings',
                                icon: '📋',
                                onClick: () => {
                                    setSelectedPatientForViewMeetings(contextMenu.patient);
                                    setIsViewMeetingsModalOpen(true);
                                    setContextMenu(null);
                                }
                            },
                            ...(contextMenu.patient.show_schedule_link ? [{
                                label: 'Open Schedule Page',
                                icon: '🗓️',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    try {
                                        const res = await apiService.getPatientScheduleLink(token, Number(patient.id));
                                        if (res?.link) {
                                            window.open(res.link, '_blank', 'noopener');
                                        }
                                    } catch (error) {
                                        console.error('Failed to get schedule link:', error);
                                    }
                                }
                            }] : []),
                            {
                                label: 'View Patient',
                                icon: '👤',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    // Navigate to patient page
                                    onNavigate?.("patient", patient.id);
                                }
                            },
                            {
                                label: 'Finish Treatment',
                                icon: (
                                    <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
                                    </svg>
                                ),
                                iconClassName: '',
                                onClick: () => {
                                    setSelectedPatientForFinishTreatment(contextMenu.patient);
                                    setIsFinishTreatmentModalOpen(true);
                                    setContextMenu(null);
                                }
                            },
                            {
                                label: 'Ad Hoc Meeting',
                                icon: '⚡',
                                onClick: () => {
                                    setSelectedPatientForAdHoc(contextMenu.patient);
                                    setContextMenu(null);
                                }
                            },
                            // Conference call to the patient, same as the "Call patient"
                            // button on the patient page.
                            ...(patientPhone ? [{
                                label: 'Call Patient',
                                icon: '📞',
                                onClick: () => {
                                    setSelectedPatientForConfCall(contextMenu.patient);
                                    setContextMenu(null);
                                }
                            }] : []),
                            ...(patientPhone ? [{
                                label: 'WhatsApp',
                                icon: '💬',
                                onClick: () => {
                                    setContextMenu(null);
                                    setWaPopupPatient({
                                        phone: normalizePhone(patientPhone),
                                        name: `${contextMenu.patient.first_name || ''} ${contextMenu.patient.last_name || ''}`.trim(),
                                        hmId: contextMenu.patient.HM_ID || contextMenu.patient.hm_id,
                                        id: contextMenu.patient.id,
                                    });
                                }
                            }] : []),
                            ...(hasPhoneExt && patientPhone ? [{
                                label: 'Call',
                                icon: '📞',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    if (patient && patient.phone) {
                                        setContextMenu(null);
                                        setStatusMessage({ type: 'loading', text: 'Initiating call...' });
                                        try {
                                            const response = await apiService.makeCall(token, patient.id, patient.main_phone);
                                            const successMsg = response.message || 'Call initiated successfully';
                                            setStatusMessage({ type: 'success', text: successMsg });
                                            setTimeout(() => setStatusMessage(null), 3000);
                                        } catch (error: any) {
                                            console.error('Failed to make call:', error);
                                            // Extract error message - prioritize error.message (from thrown errors) over response data
                                            const errorMsg = error.message || error.response?.data?.message || 'An error occurred';
                                            setStatusMessage({ type: 'error', text: errorMsg });
                                            setTimeout(() => setStatusMessage(null), 5000);
                                        }
                                    }
                                }
                            }] : []),
                            // Only clinics that charge the patient carry the links, so their
                            // presence is the gate - no clinic list on the client.
                            ...((contextMenu.patient.payment_intake_link || contextMenu.patient.payment_followup_link) ? [{
                                label: 'Payment Links',
                                icon: '\ud83d\udcb3',
                                onClick: () => {
                                    setSelectedPatientForPaymentLinks(contextMenu.patient);
                                    setContextMenu(null);
                                }
                            }] : []),
                            ...(contextMenu.patient.status === 'Waiting-for-parent2-sign' ? [{
                                label: 'Parent 2 consent link',
                                icon: '🔗',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    const consentLink = patient.parent_2_concent_link || '';
                                    if (consentLink) {
                                        navigator.clipboard.writeText(consentLink);
                                        setStatusMessage({ type: 'success', text: 'Link copied to clipboard' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } else {
                                        setStatusMessage({ type: 'error', text: 'No consent link available' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    }
                                    setContextMenu(null);
                                }
                            }] : []),
                            ...(contextMenu.patient.status === 'Waiting-for-single-guardian-verification' ? [{
                                label: 'Single guardian verification link',
                                icon: '🔗',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    const verificationLink = patient.single_guardian_link || '';
                                    if (verificationLink) {
                                        navigator.clipboard.writeText(verificationLink);
                                        setStatusMessage({ type: 'success', text: 'Link copied to clipboard' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } else {
                                        setStatusMessage({ type: 'error', text: 'No verification link available' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    }
                                    setContextMenu(null);
                                }
                            }] : [])
                        ]}
                        onClose={() => setContextMenu(null)}
                    />
                );
            })()}

            {/* Loading Overlay */}
            {isLoadingPatientData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading patient data...</p>
                    </div>
                </div>
            )}

            {/* New Meeting Modal */}
            <NewMeetingModal
                isOpen={isNewMeetingModalOpen}
                onClose={() => {
                    setIsNewMeetingModalOpen(false);
                    setSelectedPatientForNewMeeting(null);
                }}
                patient={selectedPatientForNewMeeting}
                users={propsUsers}
                meetingTypes={propsMeetingTypes}
                currentUser={user}
            />

            {/* Edit Patient Modal */}
            <EditPatientModal
                isOpen={isEditPatientModalOpen}
                onClose={() => {
                    setIsEditPatientModalOpen(false);
                    setSelectedPatientForEditInfo(null);
                }}
                patient={selectedPatientForEditInfo}
                onSave={async (updatedData: any) => {
                    setIsSavingPatientInfo(true);
                    try {
                        await apiService.updatePatientInfo(
                            token,
                            selectedPatientForEditInfo.id,
                            updatedData
                        );
                        // Refresh patient data
                        await patientsQuery.refetch();
                        // Close modal
                        setIsEditPatientModalOpen(false);
                        setSelectedPatientForEditInfo(null);
                    } catch (error: any) {
                        console.error('Failed to update patient info:', error);
                        throw new Error(error.response?.data?.message || error.message || 'Failed to update patient information');
                    } finally {
                        setIsSavingPatientInfo(false);
                    }
                }}
                isSaving={isSavingPatientInfo}
            />

            {/* View Meetings Modal */}
            <ViewMeetingsModal
                isOpen={isViewMeetingsModalOpen}
                onClose={() => {
                    setIsViewMeetingsModalOpen(false);
                    setSelectedPatientForViewMeetings(null);
                }}
                patientId={selectedPatientForViewMeetings?.id || ''}
                patientName={selectedPatientForViewMeetings ? `${selectedPatientForViewMeetings.first_name} ${selectedPatientForViewMeetings.last_name}` : ''}
                user={user}
                token={token}
                users={propsUsers as any}
                meetingTypes={propsMeetingTypes}
            />

            {/* Payment Links Modal */}
            <PaymentLinksModal
                isOpen={!!selectedPatientForPaymentLinks}
                onClose={() => setSelectedPatientForPaymentLinks(null)}
                patientName={selectedPatientForPaymentLinks
                    ? `${selectedPatientForPaymentLinks.first_name || ''} ${selectedPatientForPaymentLinks.last_name || ''}`.trim()
                    : ''}
                intakeLink={selectedPatientForPaymentLinks?.payment_intake_link}
                followupLink={selectedPatientForPaymentLinks?.payment_followup_link}
                onStatusMessage={(msg) => {
                    setStatusMessage(msg);
                    if (msg) setTimeout(() => setStatusMessage(null), 3000);
                }}
            />

            {/* Finish Treatment Modal */}
            <FinishTreatmentModal
                isOpen={isFinishTreatmentModalOpen}
                onClose={() => {
                    setIsFinishTreatmentModalOpen(false);
                    setSelectedPatientForFinishTreatment(null);
                }}
                patientId={selectedPatientForFinishTreatment?.id || 0}
                token={token}
                onSuccess={() => {
                    patientsQuery.refetch();
                }}
            />

            {/* Ad Hoc Meeting Modal */}
            {selectedPatientForAdHoc && (
                <AdHocMeetingModal
                    patientId={Number(selectedPatientForAdHoc.id)}
                    token={token}
                    meetingTypes={propsMeetingTypes}
                    onClose={() => setSelectedPatientForAdHoc(null)}
                    onSuccess={(meetingId) => {
                        const patientId = selectedPatientForAdHoc.id;
                        setStatusMessage({ type: 'success', text: 'Meeting created successfully!' });
                        setTimeout(() => setStatusMessage(null), 3000);
                        onNavigate?.("patient", patientId, meetingId);
                    }}
                    onError={(msg) => {
                        setStatusMessage({ type: 'error', text: msg });
                        setTimeout(() => setStatusMessage(null), 5000);
                    }}
                />
            )}

            {/* Call Patient Modal */}
            {selectedPatientForConfCall && (
                <CallPatientConfModal
                    patientId={selectedPatientForConfCall.id}
                    patientName={`${selectedPatientForConfCall.first_name || ''} ${selectedPatientForConfCall.last_name || ''}`.trim()}
                    patientPhone={selectedPatientForConfCall.phone}
                    parent1Phone={selectedPatientForConfCall.parent_1_phone_number}
                    parent2Phone={selectedPatientForConfCall.parent_2_phone_number}
                    token={token}
                    onClose={() => setSelectedPatientForConfCall(null)}
                    onSuccess={() => {
                        setStatusMessage({ type: 'success', text: 'Call initiated successfully!' });
                        setTimeout(() => setStatusMessage(null), 3000);
                    }}
                    onError={(msg) => {
                        setStatusMessage({ type: 'error', text: msg });
                        setTimeout(() => setStatusMessage(null), 5000);
                    }}
                />
            )}

            {/* Status Message Bar */}
            {statusMessage && (
                <div
                    className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg"
                    style={{
                        backgroundColor:
                            statusMessage.type === 'error' ? '#fee2e2' :
                                statusMessage.type === 'success' ? '#dcfce7' :
                                    '#f3f4f6',
                        color:
                            statusMessage.type === 'error' ? '#dc2626' :
                                statusMessage.type === 'success' ? '#16a34a' :
                                    '#6b7280'
                    }}
                >
                    {statusMessage.text}
                </div>
            )}
        {waPopupPatient && (
            <WhatsAppConversationPopup
                phoneNumber={waPopupPatient.phone}
                patientName={waPopupPatient.name}
                patientHmId={waPopupPatient.hmId}
                patientId={waPopupPatient.id}
                currentUserId={Number(user.id)}
                onClose={() => setWaPopupPatient(null)}
            />
        )}
        </Layout>
    );
};
